import { ErrorHandler, Injectable, NgZone, inject, OnDestroy } from '@angular/core';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler, OnDestroy {
    private readonly zone = inject(NgZone);

    // Constants
    private readonly RELOAD_KEY = 'app_reload_attempted';
    private readonly MAX_RELOAD_AGE_MS = 30_000; // 30 seconds

    // Store listener references for cleanup
    private unhandledRejectionListener: ((event: PromiseRejectionEvent) => void) | null = null;
    private errorListener: ((event: ErrorEvent) => void) | null = null;

    constructor() {
        this.setupGlobalListeners();
    }

    private setupGlobalListeners(): void {
        // Listen for unhandled Promise rejections (e.g., failed dynamic imports)
        this.unhandledRejectionListener = (event: PromiseRejectionEvent) => {
            const message = (event.reason && event.reason.message) ? event.reason.message : '';
            if (this.isChunkLoadError(message.toLowerCase())) {
                event.preventDefault();
                this.zone.runOutsideAngular(() => {
                    this.handleChunkLoadError();
                });
            }
        };

        // Listen for script load errors
        this.errorListener = (event: ErrorEvent) => {
            const message = event.message || '';
            if (this.isChunkLoadError(message.toLowerCase())) {
                event.preventDefault();
                this.zone.runOutsideAngular(() => {
                    this.handleChunkLoadError();
                });
            }
        };

        window.addEventListener('unhandledrejection', this.unhandledRejectionListener);
        window.addEventListener('error', this.errorListener as EventListener);
    }

    handleError(error: unknown): void {
        // 1. Log the error first (critical for observability tools like Sentry/Datadog)
        console.error('GlobalErrorHandler caught:', error);

        // 2. Extract message safely
        const errorMessage = this.extractErrorMessage(error).toLowerCase();

        // 3. Check for specific "Lazy Loading" failures
        if (this.isChunkLoadError(errorMessage)) {
            this.zone.runOutsideAngular(() => {
                this.handleChunkLoadError();
            });
        }
    }

    private isChunkLoadError(message: string): boolean {
        const chunkErrorPatterns = [
            'loading chunk',
            'failed to fetch dynamically imported module',
            'importing a module script failed',
            'missing script',
            'error loading dynamically imported module',
            'chunkloaderror'
        ];
        return chunkErrorPatterns.some(pattern => message.includes(pattern));
    }

    private handleChunkLoadError(): void {
        const lastReload = sessionStorage.getItem(this.RELOAD_KEY);
        const now = Date.now();

        // Loop Protection: If we reloaded < 30 seconds ago, STOP.
        if (lastReload && now - parseInt(lastReload, 10) < this.MAX_RELOAD_AGE_MS) {
            console.warn('Chunk load error persisted after reload. Stopping infinite reload loop.');
            return;
        }

        // Set flag and reload
        sessionStorage.setItem(this.RELOAD_KEY, now.toString());
        console.warn('Auto-recovering from chunk load error...');
        window.location.reload();
    }

    private extractErrorMessage(error: unknown): string {
        if (error instanceof Error) return error.message;
        if (typeof error === 'string') return error;
        return (error as any)?.message || (error as any)?.toString() || '';
    }

    ngOnDestroy(): void {
        // Clean up listeners
        if (this.unhandledRejectionListener) {
            window.removeEventListener('unhandledrejection', this.unhandledRejectionListener);
        }
        if (this.errorListener) {
            window.removeEventListener('error', this.errorListener as EventListener);
        }
    }
}